import appConfig from '../../../defs/appConfig';
import { ColorId } from '../../../game/defs/block';
import {
  blockOptionFromCountColor,
  blockOptionToCountColor,
} from '../../../game/util/blockOption';
import { BasicScreen } from '../../../lib/ui/BasicScreen';
import { ColorPicker } from '../../../lib/ui/ColorPicker';
import { NumberPicker } from '../../../lib/ui/NumberPicker';
import { Container, Sprite, Text } from '../../../odie';

export type CountColorPopupOptions = {
  onOk?: (option: number) => void;
  option: number;
  minCount?: number;
  maxCount?: number;
  colors?: ColorId[];
};

export class CountColorPopup extends BasicScreen {
  private _onOk?: (option: number) => void;
  protected onClose?: () => void;
  protected title!: Text;
  protected content!: Container;
  protected isClosing = false;
  protected countPicker!: NumberPicker;
  protected colorPicker!: ColorPicker;
  protected pickers: [NumberPicker?, ColorPicker?] = [];
  protected colors?: ColorId[] = undefined;

  public async prepare(options: CountColorPopupOptions) {
    const contentWidth = 500;
    const contentHeight = 400;

    this.colors = options.colors;
    this.root.width = appConfig.size.width;
    this.root.height = appConfig.size.height;
    this._spawnScreen();
    this._spawnContent({
      ...options,
      width: contentWidth,
      height: contentHeight,
    });
    this._spawnBackground({
      ...options,
      width: contentWidth,
      height: contentHeight,
    });

    this._onOk = options.onOk;

    const countSize = blockOptionToCountColor(options.option) ?? {
      count: 1,
      color: 'green',
    };
    this.countPicker = this._spawnPicker(
      'Count',
      countSize.count,
      options.minCount,
      options.maxCount,
    );
    this.colorPicker = this._spawnColorPicker('Color', countSize.color);

    this.title = new Text('Block Count & Color', {
      font: 'Title',
      fontSize: 30,
      align: 'center',
    });
    this.title.x = contentWidth * 0.5;
    this.title.y = 30;
    this.content.addChild(this.title);
  }

  private _spawnPicker(label: string, value: number, min = 1, max = 99) {
    const picker = new NumberPicker({
      value,
      min,
      max,
      width: 220,
      height: 70,
    });
    this.content.addChild(picker);
    picker.x = this.content.width * 0.5 - picker.width * 0.5;
    picker.y = 120 + this.pickers.length * 120;
    this.pickers.push(picker);

    const text = new Text(label, {
      font: 'Title',
      fontSize: 20,
      align: 'center',
    });
    picker.addChild(text);
    text.x = picker.width * 0.5;
    text.y = -32;

    return picker;
  }

  private _spawnColorPicker(label: string, value: ColorId) {
    const picker = new ColorPicker({
      value,
      width: 220,
      height: 70,
      colors: this.colors,
    });
    this.content.addChild(picker);
    picker.x = this.content.width * 0.5 - picker.width * 0.5;
    picker.y = 120 + this.pickers.length * 120;
    this.pickers.push(picker);

    const text = new Text(label, {
      font: 'Title',
      fontSize: 20,
      align: 'center',
    });
    picker.addChild(text);
    text.x = picker.width * 0.5;
    text.y = -32;

    return picker;
  }

  private _spawnScreen() {
    // create translucent screen
    const screen = new Sprite();
    screen.width = this.root.width;
    screen.height = this.root.height;
    screen.view.updateOpts({ backgroundColor: 'rgba(0, 0, 0, 0.8)' });
    this.root.addChild(screen);

    // tapping the screen triggers close request
    screen.setOnTap(() => this.close());
    screen.interactive = true;
  }

  private _spawnContent(options: { width: number; height: number }) {
    // spawn center pivoted for animation reasons
    const content = (this.content = new Container());
    content.width = options.width ?? 600;
    content.height = options.height ?? 600;
    content.x = this.root.width * 0.5 - content.width * 0.5;
    content.y = this.root.height * 0.5 - content.height * 0.5;
    this.root.addChild(content);
  }

  protected _spawnBackground(options: { width: number; height: number }) {
    const bg = new Container();

    bg.width = options.width ?? 600;
    bg.height = options.height ?? 600;
    bg.view.updateOpts({
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
    });

    // this blocks input from popup
    bg.interactive = true;
    this.content.addChild(bg);
  }

  public async show() {
    this.root.visible = true;
  }

  public close() {
    this.view.view.removeFromSuperview();
    const option = blockOptionFromCountColor({
      count: this.countPicker.value,
      color: this.colorPicker.value,
    });
    this._onOk?.(option);
  }
}
